import React, { useMemo, useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { Box, Avatar, Button, TextField, Skeleton, Alert, Snackbar, useTheme } from '@mui/material';
import {
  Favorite, FavoriteBorder, ChatBubbleOutline, IosShare,
  Add as AddIcon, School, People, Search, ImageOutlined, Close as CloseIcon, Event as EventIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { initials } from '../../utils/strings';
import { Post } from '../../types';
import { apiToggleLike, apiDeleteComment, apiDeletePost } from '../../services/api';
import { fetchPost, fetchPostsPage, createPost as createPostApi, addComment as addCommentApi } from '../../api/posts';
import { uploadImage, IMAGE_ACCEPT_ATTR, isSafeHttpUrl } from '../../utils/imageUpload';
import UserActionsMenu from '../../components/Moderation/UserActionsMenu';
import { formatTimeAgo } from '../../utils/formatTimeAgo';
import { MAX_POST_CHARS, MAX_COMMENT_CHARS, FEED_CLAMP_LINES } from '../../constants';

const SUPER_ADMIN_EMAIL = 'hl865@drexel.edu';
const AVATAR_GRAD = 'linear-gradient(140deg,#FFC24B 0%,#FF7A36 60%,#FF5B2E 100%)';
const BTN_GRAD = 'linear-gradient(120deg,#FFB23E 0%,#FF6A3D 100%)';

// Feed design tokens (Claude Design "Dragonsframe Home"): ember on ink in dark, blue on
// paper in light. Self-contained so the feed matches the mock regardless of MUI palette.
function useFeedTokens() {
  const dark = useTheme().palette.mode === 'dark';
  return useMemo(() => (dark ? {
    dark: true, text: '#F2ECE2', heading: '#F7F2EA', muted: '#9E968B', accent: '#FF6A3D',
    cardBg: 'rgba(255,255,255,0.04)', cardBorder: 'rgba(255,255,255,0.10)', cardShadow: 'none',
    delete: '#FF6F5E', btnText: '#2A1306', qaText: '#FF7A4D', qaHoverBg: 'rgba(255,106,61,0.12)',
  } : {
    dark: false, text: '#23263A', heading: '#15172A', muted: '#6B7180', accent: '#26408B',
    cardBg: '#FFFFFF', cardBorder: '#E7E9F1', cardShadow: '0 1px 2px rgba(20,24,60,0.05), 0 12px 28px -20px rgba(20,24,60,0.22)',
    delete: '#D62839', btnText: '#FFFFFF', qaText: '#26408B', qaHoverBg: 'rgba(38,64,139,0.06)',
  }), [dark]);
}

const Home: React.FC = () => {
  const { user } = useAuth();
  const c = useFeedTokens();
  const isSuperAdmin = String(user?.email || '').toLowerCase() === SUPER_ADMIN_EMAIL;
  const navigate = useNavigate();
  const [posts, setPosts] = useState<Post[] | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);

  // Shared links + notification deep-links land here as /?post=<id>. Fetch that post,
  // pin it on top of the feed if it isn't already loaded, highlight and scroll to it.
  const [highlightedPostId, setHighlightedPostId] = useState<string | null>(null);
  React.useEffect(() => {
    let alive = true;
    (async () => {
      const sharedId = new URLSearchParams(window.location.search).get('post');
      let shared: Post | null = null;
      if (sharedId) {
        try { shared = await fetchPost(sharedId); } catch { /* deleted/foreign post — just show the feed */ }
      }
      try {
        const page = await fetchPostsPage({ globalOnly: true, take: 12 });
        if (!alive) return;
        let items = page.items as Post[];
        if (shared) {
          const s = shared;
          items = [s, ...items.filter((p) => p.id !== s.id)];
        }
        setError(null); setPosts(items); setNextCursor(page.nextCursor);
      } catch (e: any) {
        if (!alive) return;
        if (shared) { setError(null); setPosts([shared]); setNextCursor(null); }
        else { setError(e?.message ?? 'Failed to load feed'); setPosts([]); setNextCursor(null); }
      }
      if (shared && alive) {
        const s = shared;
        setHighlightedPostId(s.id);
        setTimeout(() => document.getElementById(`post-${s.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 200);
        setTimeout(() => { if (alive) setHighlightedPostId(null); }, 4000);
      }
    })();
    return () => { alive = false; };
  }, []);

  const [newPostContent, setNewPostContent] = useState('');
  const postRemaining = MAX_POST_CHARS - newPostContent.length;
  const postTooLong = postRemaining < 0;
  const createPostRef = React.useRef<HTMLDivElement | null>(null);
  const [activeCommentPostId, setActiveCommentPostId] = useState<string | null>(null);
  const [commentText, setCommentText] = useState('');
  const commentRemaining = MAX_COMMENT_CHARS - commentText.length;
  const commentTooLong = commentRemaining < 0;
  const [replyingToId, setReplyingToId] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const replyRemaining = MAX_COMMENT_CHARS - replyText.length;
  const replyTooLong = replyRemaining < 0;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCreatingPost, setIsCreatingPost] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [postImageUrl, setPostImageUrl] = useState<string | null>(null);
  const [postImageUploading, setPostImageUploading] = useState(false);
  const [postImageError, setPostImageError] = useState<string | null>(null);
  const postFileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [blockedAuthorIds, setBlockedAuthorIds] = useState<string[]>([]);
  const [reportToast, setReportToast] = useState<string | null>(null);

  const handlePostImageFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setPostImageError(null);
    setPostImageUploading(true);
    try {
      const url = await uploadImage(file);
      setPostImageUrl(url);
    } catch (err: any) {
      setPostImageError(err?.message ?? 'Upload failed. Please try again.');
    } finally {
      setPostImageUploading(false);
    }
  };
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [confirmDeletePostId, setConfirmDeletePostId] = useState<string | null>(null);

  const handleLike = async (postId: string) => {
    try {
      const res = await apiToggleLike(postId);
      setPosts(prev => (prev ?? []).map(p => p.id === postId ? { ...p, likes: res.data } : p));
    } catch (e) { console.error('Like failed', e); }
  };

  const handleShare = async (postId: string) => {
    const url = `${window.location.origin}/?post=${postId}`;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        // Fallback for browsers / contexts without the async clipboard API.
        const ta = document.createElement('textarea');
        ta.value = url; ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.focus(); ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setReportToast('Link copied to clipboard.');
    } catch (e) {
      // Clipboard is unavailable (rare outside a secure context); avoid a misleading
      // success-styled toast and just log.
      console.error('Share copy failed', e);
    }
  };

  const handleCreatePost = async () => {
    const draft = newPostContent.trim();
    if ((!draft && !postImageUrl) || !user || postTooLong || postImageUploading) return;
    const image = postImageUrl;
    const tempId = `tmp-post-${Date.now()}`;
    const optimisticPost: Post = {
      id: tempId, authorId: user.id,
      author: { ...user, createdAt: user.createdAt ?? new Date(), updatedAt: user.updatedAt ?? new Date() } as any,
      content: draft, imageUrl: image, likes: [], comments: [], tags: [], createdAt: new Date(), updatedAt: new Date(),
    };
    setPosts(prev => [optimisticPost, ...(prev ?? [])]);
    setNewPostContent(''); setPostImageUrl(null); setPostImageError(null); setSubmitError(null); setIsCreatingPost(true);
    try {
      const created = await createPostApi(draft, image);
      setPosts(prev => (prev ?? []).map(p => (p.id === tempId ? (created as Post) : p)));
    } catch (e: any) {
      setPosts(prev => (prev ?? []).filter(p => p.id !== tempId));
      setNewPostContent(draft);
      setPostImageUrl(image);
      const msg = String(e?.message || '');
      setSubmitError(msg.includes('413') || msg.includes('content_too_long') ? `Post too long. Max ${MAX_POST_CHARS} characters.` : 'Failed to publish post. Please try again.');
    } finally { setIsCreatingPost(false); }
  };

  const handleOpenComments = (postId: string) => { setActiveCommentPostId(prev => (prev === postId ? null : postId)); setCommentText(''); };

  const handleAddComment = async (postId: string) => {
    if (isSubmitting || commentTooLong) return;
    const trimmed = commentText.trim();
    if (!trimmed || !user) return;
    try {
      setIsSubmitting(true);
      const created = await addCommentApi(postId, trimmed);
      setPosts(prev => (prev ?? []).map(p => p.id === postId ? { ...p, comments: [...(p.comments ?? []), created as any] } : p));
      setCommentText(''); setSubmitError(null);
    } catch (e) { setSubmitError((e as any)?.message ?? 'Failed to comment'); }
    finally { setIsSubmitting(false); }
  };

  const handleReply = async (postId: string, parentId: string) => {
    if (isSubmitting || replyTooLong) return;
    const trimmed = replyText.trim();
    if (!trimmed || !user) return;
    try {
      setIsSubmitting(true);
      const created = await addCommentApi(postId, trimmed, parentId);
      setPosts(prev => (prev ?? []).map(p => p.id !== postId ? p : { ...p, comments: p.comments.map(c2 => c2.id === parentId ? { ...c2, children: [...c2.children, created as any] } : c2) }));
      setReplyText(''); setReplyingToId(null); setSubmitError(null);
    } catch (e) { setSubmitError((e as any)?.message ?? 'Failed to reply'); }
    finally { setIsSubmitting(false); }
  };

  function removeCommentFromTree(node: any, targetId: string): any | null {
    if (!node) return node;
    if (node.id === targetId) return null;
    return { ...node, children: (node.children ?? []).map((ch: any) => removeCommentFromTree(ch, targetId)).filter(Boolean) };
  }
  function countCommentsWithReplies(nodes: any[] = []): number {
    return nodes.reduce((tot, n) => tot + 1 + countCommentsWithReplies(n?.children ?? []), 0);
  }

  // Both deletes are optimistic: close the confirm popover and remove the item
  // immediately (no waiting on the network), then reconcile/roll back on failure.
  const handleDeleteComment = async (postId: string, commentId: string) => {
    const previous = posts ?? [];
    setConfirmDeleteId(null);
    setPosts(prev => (prev ?? []).map(p => p.id !== postId ? p : { ...p, comments: (p.comments ?? []).map(c2 => removeCommentFromTree(c2, commentId)).filter(Boolean) as any[] }));
    try { await apiDeleteComment(commentId); }
    catch (e) { setPosts(previous); setReportToast('Could not delete the comment.'); }
  };

  const handleDeletePost = async (postId: string) => {
    const previous = posts ?? [];
    setConfirmDeletePostId(null);
    setPosts(prev => (prev ?? []).filter(p => p.id !== postId));
    try { await apiDeletePost(postId); }
    catch (e) { setPosts(previous); setReportToast('Could not delete the post.'); }
  };

  const handleLoadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadMoreError(null); setLoadingMore(true);
    try {
      const page = await fetchPostsPage({ globalOnly: true, cursor: nextCursor, take: 12 });
      setPosts(prev => {
        const existing = prev ?? [];
        const seen = new Set(existing.map(p => p.id));
        return [...existing, ...page.items.filter(p => !seen.has(p.id))];
      });
      setNextCursor(page.nextCursor);
    } catch (e: any) { setLoadMoreError(e?.message ?? 'Failed to load more posts'); }
    finally { setLoadingMore(false); }
  };

  // Composer enabled-state, computed once (referenced by the Post button's disabled + styles).
  const postEmpty = !newPostContent.trim() && !postImageUrl;
  const postInactive = postEmpty || postTooLong;
  const postDisabled = isCreatingPost || postImageUploading || postInactive;

  // ---- styles ----
  const avatarSx = (size = 44) => ({ width: size, height: size, fontWeight: 700, fontSize: size * 0.34, background: AVATAR_GRAD, color: '#fff', boxShadow: '0 4px 12px -4px rgba(255,106,61,0.5)' });
  const cardSx = { background: c.cardBg, border: `1px solid ${c.cardBorder}`, borderRadius: '16px', p: { xs: 2, sm: '22px' }, boxShadow: c.cardShadow, boxSizing: 'border-box' as const };
  const qaSx = {
    display: 'flex', alignItems: 'center', gap: 1.25, px: { xs: '14px', sm: '17px' }, py: { xs: '13px', sm: '15px' },
    borderRadius: '13px', border: `1px solid ${c.cardBorder}`, background: c.cardBg, color: c.qaText,
    fontWeight: 600, fontSize: { xs: 13.5, sm: 14.5 }, cursor: 'pointer', fontFamily: 'inherit', boxShadow: c.cardShadow,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'left' as const, lineHeight: 1.2,
    transition: 'transform .15s, border-color .2s, background .2s',
    '&:hover': { transform: 'translateY(-2px)', borderColor: c.accent, background: c.qaHoverBg },
  };
  const metaFor = (p: Post) => {
    const a: any = p.author || {};
    const parts = [a.major, a.year ? `Year ${a.year}` : null, formatTimeAgo(p.createdAt)].filter(Boolean);
    return parts.join(' · ');
  };

  const PostBody: React.FC<{ content: string }> = ({ content }) => {
    const [expanded, setExpanded] = React.useState(false);
    const [hasOverflow, setHasOverflow] = React.useState(false);
    const bodyRef = React.useRef<HTMLDivElement>(null);
    React.useLayoutEffect(() => {
      // Measure synchronously so the "Show more" button commits before first paint —
      // an rAF here made clamped posts grow a frame later, nudging the whole feed.
      const el = bodyRef.current; if (!el) return;
      setHasOverflow(el.scrollHeight > el.clientHeight + 1);
    }, [content, expanded]);
    if (!content) return null;
    return (
      <Box sx={{ mt: '13px' }}>
        <Box ref={bodyRef} sx={{ fontSize: 15, lineHeight: 1.55, color: c.text, whiteSpace: 'pre-line',
          ...(expanded ? {} : { display: '-webkit-box', WebkitLineClamp: FEED_CLAMP_LINES, WebkitBoxOrient: 'vertical', overflow: 'hidden' }) }}>
          {content}
        </Box>
        {hasOverflow && (
          <Box component="button" onClick={() => setExpanded(v => !v)} sx={{ mt: 0.5, background: 'none', border: 'none', p: 0, cursor: 'pointer', color: c.accent, fontWeight: 600, fontSize: 13.5, fontFamily: 'inherit' }}>
            {expanded ? 'Show less' : 'Show more'}
          </Box>
        )}
      </Box>
    );
  };

  const actionChip = (icon: React.ReactNode, label: React.ReactNode, color: string, onClick?: () => void) => (
    <Box component={onClick ? 'button' : 'span'} onClick={onClick}
      sx={{ display: 'inline-flex', alignItems: 'center', gap: '7px', fontSize: 13.5, fontWeight: 600, color, background: 'none', border: 'none', p: 0, cursor: onClick ? 'pointer' : 'default', fontFamily: 'inherit' }}>
      {icon}<span>{label}</span>
    </Box>
  );

  const skeleton = (
    <Box sx={cardSx}>
      <Box sx={{ display: 'flex', gap: 1.6, alignItems: 'center' }}>
        <Skeleton variant="circular" width={44} height={44} />
        <Box sx={{ flex: 1 }}><Skeleton width={160} height={22} /><Skeleton width={220} height={16} /></Box>
      </Box>
      <Skeleton variant="rectangular" height={48} sx={{ mt: 2, borderRadius: 1 }} />
    </Box>
  );

  return (
    <Box sx={{ width: '100%', maxWidth: 1040, mx: 'auto', display: 'flex', flexDirection: 'column', gap: { xs: 2.25, sm: '22px' } }}>
      {/* welcome */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box component="h1" sx={{ m: 0, fontWeight: 800, fontSize: { xs: 27, sm: 40 }, lineHeight: 1.08, letterSpacing: '-0.02em', color: c.heading }}>
          Welcome back, {user?.firstName || 'there'}! <Box component="span" sx={{ fontWeight: 400 }}>👋</Box>
        </Box>
        <Box component="p" sx={{ m: 0, fontSize: { xs: 14.5, sm: 16 }, color: c.muted, lineHeight: 1.5 }}>
          Connect with your fellow students and stay updated with campus life.
        </Box>
      </Box>

      {/* quick actions */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' }, gap: 1.5 }}>
        <Box component="button" sx={qaSx} onClick={() => navigate('/courses')}><School sx={{ fontSize: 19 }} /> Course Channels</Box>
        <Box component="button" sx={qaSx} onClick={() => navigate('/events')}><EventIcon sx={{ fontSize: 19 }} /> Campus Events</Box>
        <Box component="button" sx={qaSx} onClick={() => navigate('/roommates')}><People sx={{ fontSize: 19 }} /> Find Roommates</Box>
        <Box component="button" sx={qaSx} onClick={() => navigate('/search')}><Search sx={{ fontSize: 19 }} /> Search Students</Box>
        <Box component="button" sx={{ ...qaSx, gridColumn: { xs: '1 / -1', sm: 'auto' } }} onClick={() => createPostRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}><AddIcon sx={{ fontSize: 19 }} /> Create Post</Box>
      </Box>

      {/* composer */}
      <Box ref={createPostRef} sx={cardSx}>
        <Box sx={{ display: 'flex', gap: 1.75, alignItems: 'flex-start' }}>
          <Avatar src={(user as any)?.profileImage || undefined} sx={avatarSx(44)}>{initials(user?.displayName || `${user?.firstName || ''} ${user?.lastName || ''}`)}</Avatar>
          <TextField fullWidth multiline minRows={3} variant="standard" placeholder="What's on your mind?"
            value={newPostContent} onChange={(e) => setNewPostContent(e.target.value)} disabled={isCreatingPost}
            InputProps={{ disableUnderline: true, sx: { fontSize: 16, lineHeight: 1.5, color: c.text } }} sx={{ mt: 0.5 }} />
        </Box>
        {(postImageUrl || postImageUploading) && (
          <Box sx={{ mt: 1.5, position: 'relative', display: 'inline-block', maxWidth: '100%' }}>
            {postImageUploading ? (
              <Box sx={{ width: 180, height: 120, borderRadius: '12px', border: `1px solid ${c.cardBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: c.muted, fontSize: 13 }}>
                Uploading…
              </Box>
            ) : (
              <>
                <Box component="img" src={postImageUrl!} alt="Attachment preview"
                  sx={{ maxWidth: '100%', maxHeight: 280, borderRadius: '12px', border: `1px solid ${c.cardBorder}`, display: 'block' }} />
                <Box component="button" type="button" onClick={() => { setPostImageUrl(null); setPostImageError(null); }} aria-label="Remove image"
                  sx={{ position: 'absolute', top: 8, right: 8, width: 28, height: 28, borderRadius: '50%', border: 'none', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', color: '#fff' }}>
                  <CloseIcon sx={{ fontSize: 17 }} />
                </Box>
              </>
            )}
          </Box>
        )}
        {postImageError && <Box sx={{ mt: 1, fontSize: 13, color: c.delete }}>{postImageError}</Box>}
        <input ref={postFileInputRef} hidden type="file" accept={IMAGE_ACCEPT_ATTR} onChange={handlePostImageFile} />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1, gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Box component="button" type="button" onClick={() => postFileInputRef.current?.click()} disabled={postImageUploading || isCreatingPost}
              aria-label="Add a photo"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, background: 'none', border: 'none', p: '4px 6px', borderRadius: '8px',
                cursor: (postImageUploading || isCreatingPost) ? 'default' : 'pointer', color: c.muted, fontFamily: 'inherit', fontSize: 13.5, fontWeight: 600,
                transition: 'color .2s, background .2s', '&:hover': { color: c.accent, background: c.qaHoverBg } }}>
              <ImageOutlined sx={{ fontSize: 19 }} /> Photo
            </Box>
            <Box sx={{ fontSize: 13, color: postTooLong ? c.delete : c.muted }}>{Math.max(postRemaining, -9999)} chars left</Box>
          </Box>
          <Box component="button" onClick={handleCreatePost} disabled={postDisabled}
            sx={{ px: 3, py: '10px', borderRadius: '11px', border: 'none', fontFamily: 'inherit', fontWeight: 700, fontSize: 14.5, flexShrink: 0,
              cursor: postDisabled ? 'default' : 'pointer',
              color: postInactive ? c.muted : c.btnText,
              background: postInactive ? c.cardBorder : BTN_GRAD,
              boxShadow: postInactive ? 'none' : '0 8px 20px -8px rgba(255,106,61,0.6)',
              transition: 'filter .2s, transform .15s', '&:hover': postInactive ? {} : { filter: 'brightness(1.06)', transform: 'translateY(-1px)' } }}>
            {isCreatingPost ? 'Posting…' : 'Post'}
          </Box>
        </Box>
        {submitError && <Alert severity="error" sx={{ mt: 2 }}>{submitError}</Alert>}
      </Box>

      {/* recent posts */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
        <Box component="h2" sx={{ m: '2px 0 0', fontWeight: 800, fontSize: 20, letterSpacing: '-0.01em', color: c.heading }}>Recent Posts</Box>

        {posts === null && <>{skeleton}{skeleton}{skeleton}</>}
        {error && posts !== null && <Alert severity="error">{error}</Alert>}
        {posts !== null && !error && posts.length === 0 && (
          <Box sx={{ ...cardSx, textAlign: 'center', color: c.muted }}>No posts yet — be the first to post!</Box>
        )}

        {posts !== null && !error && posts
          .filter(p => !!p && !!p.author && typeof p.author.displayName === 'string')
          .filter(p => !blockedAuthorIds.includes(p.author?.id || ''))
          .map((post) => {
          const liked = post.likes.includes(user?.id || '');
          const isMyPost = !!user?.id && post.author?.id === user.id;
          const canDelete = !!user?.id && (post.author?.id === user.id || isSuperAdmin);
          return (
            <Box key={post.id} id={`post-${post.id}`} sx={{ ...cardSx, ...(highlightedPostId === post.id ? { outline: `2px solid ${c.accent}`, outlineOffset: '2px', transition: 'outline-color .3s' } : {}) }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.6 }}>
                <Avatar component={RouterLink as any} to={`/profile/${post.author.id}`} src={(post.author as any)?.profileImage || undefined} sx={{ ...avatarSx(44), textDecoration: 'none' }} onClick={(e: any) => e.stopPropagation()}>
                  {initials(post.author?.displayName)}
                </Avatar>
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <Box component={RouterLink as any} to={`/profile/${post.author?.id ?? ''}`} sx={{ fontWeight: 700, fontSize: 15.5, color: c.accent, lineHeight: 1.2, textDecoration: 'none' }}>
                    {post.author?.displayName ?? 'Unknown Student'}
                  </Box>
                  <Box sx={{ fontSize: 12.5, color: c.muted }}>{metaFor(post)}</Box>
                </Box>
                <Box sx={{ flex: 1 }} />
                {canDelete && (
                  <Box component="button" onClick={() => setConfirmDeletePostId(post.id)} sx={{ background: 'none', border: 'none', cursor: 'pointer', color: c.delete, fontSize: 13, fontWeight: 600, fontFamily: 'inherit', p: '2px 4px' }}>Delete</Box>
                )}
                {!isMyPost && post.author?.id && (
                  <UserActionsMenu
                    targetUserId={post.author.id}
                    targetUserName={post.author.displayName}
                    report={{ targetType: 'post', targetId: post.id }}
                    onBlocked={() => setBlockedAuthorIds(prev => prev.includes(post.author.id) ? prev : [...prev, post.author.id])}
                    onReported={() => setReportToast('Report submitted. Thanks for helping keep the community safe.')}
                    iconColor={c.muted}
                  />
                )}
              </Box>

              <PostBody content={post.content} />

              {post.imageUrl && isSafeHttpUrl(post.imageUrl) && (
                <Box sx={{ mt: '13px' }}>
                  <Box component="a" href={post.imageUrl} target="_blank" rel="noopener noreferrer" aria-label="Open image in new tab" sx={{ display: 'block' }}>
                    {/* Fixed-aspect frame reserves the space BEFORE the image decodes, so the
                        feed never push-reflows as lazy images load while scrolling. */}
                    <Box sx={{ aspectRatio: '16 / 10', width: '100%', overflow: 'hidden', borderRadius: '12px', border: `1px solid ${c.cardBorder}`, background: c.cardBorder }}>
                      <Box
                        component="img"
                        src={post.imageUrl}
                        alt="Post attachment"
                        loading="lazy"
                        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', cursor: 'pointer' }}
                      />
                    </Box>
                  </Box>
                </Box>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mt: '15px' }}>
                {actionChip(liked ? <Favorite sx={{ fontSize: 18 }} /> : <FavoriteBorder sx={{ fontSize: 18 }} />, post.likes.length, liked ? c.accent : c.muted, () => handleLike(post.id))}
                {actionChip(<ChatBubbleOutline sx={{ fontSize: 18 }} />, countCommentsWithReplies(post.comments), activeCommentPostId === post.id ? c.accent : c.muted, () => handleOpenComments(post.id))}
                {actionChip(<IosShare sx={{ fontSize: 18 }} />, 'Share', c.muted, () => handleShare(post.id))}
              </Box>

              {/* comments (inline) */}
              {activeCommentPostId === post.id && (
                <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${c.cardBorder}` }}>
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <Avatar src={(user as any)?.profileImage || undefined} sx={avatarSx(36)}>{initials(user?.displayName || `${user?.firstName || ''} ${user?.lastName || ''}`)}</Avatar>
                    <TextField fullWidth size="small" placeholder="Write a comment..." value={commentText} onChange={(e) => setCommentText(e.target.value)} multiline minRows={1} />
                    <Button variant="contained" disabled={isSubmitting || commentTooLong || !commentText.trim()} onClick={() => handleAddComment(post.id)}>
                      {isSubmitting ? '…' : 'Comment'}
                    </Button>
                  </Box>
                  {post.comments.map((cm) => (
                    <Box key={cm.id} sx={{ mb: 1.5 }}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Avatar src={(cm.author as any)?.profileImage || undefined} sx={avatarSx(32)}>{initials(cm.author?.displayName)}</Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ fontWeight: 700, fontSize: 14, color: c.text }}>{cm.author.displayName}</Box>
                          <Box sx={{ fontSize: 14, color: c.text, whiteSpace: 'pre-line', mb: 0.5 }}>{cm.isDeleted ? '(deleted)' : cm.content}</Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ fontSize: 12, color: c.muted }}>{formatTimeAgo(cm.createdAt)}</Box>
                            {user && <Box component="button" onClick={() => { setReplyingToId(cm.id); setReplyText(''); }} sx={{ background: 'none', border: 'none', cursor: 'pointer', color: c.accent, fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit', p: 0 }}>Reply</Box>}
                            {!!user?.id && (cm.author?.id === user.id || isSuperAdmin) && (
                              <Box component="button" onClick={() => setConfirmDeleteId(cm.id)} sx={{ background: 'none', border: 'none', cursor: (cm.children?.length ?? 0) > 0 ? 'default' : 'pointer', color: c.delete, fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit', p: 0, opacity: (cm.children?.length ?? 0) > 0 ? 0.4 : 1 }} title={(cm.children?.length ?? 0) > 0 ? 'Cannot delete a comment with replies' : undefined}>Delete</Box>
                            )}
                          </Box>
                          {replyingToId === cm.id && (
                            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                              <TextField fullWidth size="small" placeholder={`Reply to ${cm.author.displayName}...`} value={replyText} onChange={(e) => setReplyText(e.target.value)} multiline minRows={1} />
                              <Button variant="contained" disabled={isSubmitting || replyTooLong || !replyText.trim()} onClick={() => handleReply(post.id, cm.id)}>{isSubmitting ? '…' : 'Reply'}</Button>
                            </Box>
                          )}
                          {cm.children && cm.children.length > 0 && (
                            <Box sx={{ mt: 1, pl: 4 }}>
                              {cm.children.map(child => (
                                <Box key={child.id} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                  <Avatar src={(child.author as any)?.profileImage || undefined} sx={avatarSx(28)}>{initials(child.author?.displayName)}</Avatar>
                                  <Box>
                                    <Box sx={{ fontWeight: 700, fontSize: 13.5, color: c.text }}>{child.author.displayName}</Box>
                                    <Box sx={{ fontSize: 13.5, color: c.text, whiteSpace: 'pre-line' }}>{child.isDeleted ? '(deleted)' : child.content}</Box>
                                    <Box sx={{ fontSize: 11.5, color: c.muted }}>{formatTimeAgo(child.createdAt)}</Box>
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          );
        })}

        {posts !== null && !error && posts.length > 0 && nextCursor && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
            <Button variant="outlined" onClick={handleLoadMore} disabled={loadingMore}>{loadingMore ? 'Loading…' : 'Load more'}</Button>
          </Box>
        )}
        {posts !== null && !error && loadMoreError && <Alert severity="warning">{loadMoreError}</Alert>}
      </Box>

      {confirmDeleteId && (
        <Box sx={{ position: 'fixed', bottom: 88, right: 24, zIndex: 1400, ...cardSx, background: c.dark ? '#241C15' : '#FFFFFF', boxShadow: '0 20px 50px -20px rgba(0,0,0,0.6)' }}>
          <Box sx={{ fontWeight: 700, mb: 1, color: c.text }}>Delete this comment?</Box>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={() => setConfirmDeleteId(null)}>Cancel</Button>
            <Button size="small" color="error" onClick={() => {
              const p = (posts ?? []).find(p => p.comments.some(cm => cm.id === confirmDeleteId) || p.comments.some(cm => (cm.children ?? []).some(ch => ch.id === confirmDeleteId)));
              if (p) handleDeleteComment(p.id, confirmDeleteId); else setConfirmDeleteId(null);
            }}>Delete</Button>
          </Box>
        </Box>
      )}

      {confirmDeletePostId && (
        <Box sx={{ position: 'fixed', bottom: 88, left: 24, zIndex: 1400, ...cardSx, background: c.dark ? '#241C15' : '#FFFFFF', boxShadow: '0 20px 50px -20px rgba(0,0,0,0.6)' }}>
          <Box sx={{ fontWeight: 700, mb: 1, color: c.text }}>Delete this post?</Box>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={() => setConfirmDeletePostId(null)}>Cancel</Button>
            <Button size="small" color="error" onClick={() => confirmDeletePostId && handleDeletePost(confirmDeletePostId)}>Delete</Button>
          </Box>
        </Box>
      )}

      <Snackbar
        open={!!reportToast}
        autoHideDuration={4000}
        onClose={() => setReportToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: { xs: 80 } }} /* clear the 56px fixed bottom nav */
      >
        <Alert severity="success" variant="filled" onClose={() => setReportToast(null)} sx={{ width: '100%' }}>
          {reportToast}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Home;
